import { isEqual } from 'lodash';
import { findParentNodeClosestToPos } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { Transaction } from '@tiptap/pm/state';

import { TiptapNodeType } from '../../tiptapTypes';
import type { TaskItemChipAttrs } from './taskListItemChipTypes';

/**
 * Every chip attr at its schema default.
 *
 * `taskId` is deliberately absent: the item is still the same row in the list, and its id keys
 * chip selection and reminder reconciliation.
 */
const CLEARED_TASK_ITEM_CHIP_ATTRS: Omit<TaskItemChipAttrs, 'taskId'> = {
    dueDate: null,
    assignments: [],
    labels: [],
    reactions: [],
    showAssignments: false,
    showDueDate: false,
};

/** ProseMirror's own cut handler marks the deletion it dispatches after writing the clipboard. */
const isCutTransaction = (transaction: Transaction): boolean => transaction.getMeta('uiEvent') === 'cut';

/**
 * Clears a task's chips when a cut leaves its row empty.
 *
 * Cutting the whole of a task reads as taking the task, not just its words - the chips travel with
 * it on the clipboard (see `createTaskListItemCopyExpansionPlugin`), so leaving a due date or an
 * assignee stranded on the now-empty row would duplicate them once the cut is pasted.
 *
 * A cut spanning several tasks is the same story. ProseMirror deletes forwards from the selection's
 * start, merging what survives into the task that held it, so that one task is left behind holding
 * its own chips - and it is the only row a cut can empty. Which is why this looks at the resulting
 * row rather than at the shape of the selection: whether the cut took one task or ten, the question
 * is the same.
 *
 * Only a cut is treated this way. Selecting the same text and deleting it is ordinary editing, and
 * a user retyping a task's wording should keep its chips.
 */
export const createTaskListItemCutChipRemovalPlugin = (): Plugin =>
    new Plugin({
        key: new PluginKey('taskListItemCutChipRemoval'),
        appendTransaction: (transactions, oldState, newState) => {
            if (!transactions.some(isCutTransaction)) return null;

            const cutFrom = findParentNodeClosestToPos(
                oldState.selection.$from,
                (node) => node.type.name === TiptapNodeType.taskItem,
            );

            // A cut starting outside a task, such as one taking the list's title with it.
            if (!cutFrom) return null;

            // Everything the cut removed lies at or after the selection's start, which is inside
            // this task, so the task's own position survives the deletion unmoved.
            const taskItem = newState.doc.nodeAt(cutFrom.pos);

            if (taskItem?.type.name !== TiptapNodeType.taskItem) return null;

            // A task's own text is its first child. Text left there means the cut took part of a
            // task rather than the whole of it, counting the tail a multi-task cut merges back in.
            if (taskItem.firstChild?.content.size) return null;

            const attrs = { ...taskItem.attrs, ...CLEARED_TASK_ITEM_CHIP_ATTRS };

            // A chipless task would otherwise gain an undo step, and a save, for no change.
            if (isEqual(attrs, taskItem.attrs)) return null;

            return newState.tr.setNodeMarkup(cutFrom.pos, undefined, attrs);
        },
    });
